/**
 * Maps Arabic upload / confirmation titles to generic document illustrations in /public/new/.
 * Normalizes common spelling variants (ة/ه، ى/ي، hamza forms) for fuzzy matching.
 */

function newAssetPath(file: string): string {
  const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  return `${base}/new/${file}`;
}

/** Strip tatweel, diacritics; unify common letter variants for substring checks */
export function normalizeArabicDocumentHint(text: string): string {
  return text
    .replace(/\u0640/g, '')
    .replace(/[\u064B-\u065F\u0670]/g, '')
    .replace(/[إأآٱ]/g, 'ا')
    .replace(/ى/g, 'ي')
    .replace(/ة/g, 'ه')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Returns public URL for a placeholder PNG, or null to keep showing the real preview.
 */
export function getDocumentUploadPlaceholderSrc(titleOrLabel: string): string | null {
  if (!titleOrLabel?.trim()) return null;

  const t = normalizeArabicDocumentHint(titleOrLabel);

  const nationalContext =
    t.includes('القومي') ||
    t.includes('قمي') ||
    t.includes('بطاقه') ||
    t.includes('بطاقة');

  const carLicenseContext =
    t.includes('رخصه') &&
    (t.includes('السياره') ||
      t.includes('السيارة') ||
      t.includes('العربيه') ||
      t.includes('العربية') ||
      t.includes('سياره') ||
      t.includes('سيارة'));

  const drivingLicenseContext =
    t.includes('رخصه') &&
    (t.includes('القياده') || t.includes('قياده') || t.includes('القيادة') || t.includes('قيادة')) &&
    !carLicenseContext;

  const backWords =
    t.includes('ظهر') || t.includes('خلفي') || t.includes('خلفى') || t.includes('الخلفي');
  const frontWords =
    t.includes('وش') ||
    t.includes('وجه') ||
    t.includes('امامي') ||
    t.includes('أمامي') ||
    t.includes('الامامي') ||
    t.includes('الأمامي');

  // Explicit phrases (user-requested + common variants)
  if (t.includes('وجه البطاقه') || t.includes('وجه بطاقه')) {
    return newAssetPath('ID-front.png');
  }
  if (t.includes('ظهر البطاقه') || t.includes('ظهر بطاقه')) {
    return newAssetPath('ID-back.png');
  }
  if (t.includes('وجه رخصه السياره') || t.includes('وجه رخصة السيارة')) {
    return newAssetPath('car-license-front.png');
  }

  // Vehicle (car) license — before generic "رخصه" rules
  if (carLicenseContext) {
    if (backWords) return newAssetPath('car-license-back.png');
    if (frontWords) return newAssetPath('car-license-front.png');
    return null;
  }

  if (drivingLicenseContext || t.includes('رخصه القياده') || t.includes('رخصة القيادة')) {
    return newAssetPath('driving-license.png');
  }

  if (nationalContext) {
    if (backWords) return newAssetPath('ID-back.png');
    if (frontWords) return newAssetPath('ID-front.png');
    return null;
  }

  return null;
}
